import { useEffect, useState } from "react";
import {
  getUserTypes,
  createUserType,
  updateUserType,
  deleteUserType,
} from "../../api/userTypesApi";
import AddUserTypeModal from "./AddUserTypeModal";
import ToastContainer from "../../components/toastContainer";
import Toast from "../../components/toast";
import PageLoader from "../../components/pageLoader";
import { PencilIcon, TrashIcon } from "@heroicons/react/outline";
import ConfirmDeleteModal from "./ConfirmDeleteModal";
import { useAuthStore } from "../../store/auth.store";

const PERMISSIONS_SCHEMA = [
  {
    key: "users_data",
    label: "Users & Data",
    children: [
      { key: "users", label: "Users" },
      { key: "user_types", label: "User Types" },
      { key: "companies_info", label: "Companies Info" },
    ],
  },
  {
    key: "promoters",
    label: "Promoters",
    children: [],
  },
  {
    key: "artists",
    label: "Artists",
    children: [],
  },
  {
    key: "documents",
    label: "Documents",
    children: [],
  },
  {
    key: "ticketing",
    label: "Ticketing Platform",
    children: [
      { key: "events", label: "Events" },
      { key: "orders", label: "Orders" },
      { key: "clients", label: "Clients" },
      { key: "sales_tags", label: "Sales Tags" },
    ],
  },
  {
    key: "bookings",
    label: "Bookings Management",
    children: [],
  },
  {
    key: "marketing",
    label: "Marketing",
    children: [
      { key: "landing_pages", label: "Landing Pages" },
      { key: "links", label: "Shorten Links & QRs" },
      { key: "pixels", label: "Pixels" },
    ],
  },
];

const buildDefaultPermissions = () => {
  const perms = {};
  PERMISSIONS_SCHEMA.forEach((group) => {
    perms[group.key] = false;
    group.children.forEach((child) => {
      perms[child.key] = false;
    });
  });
  return perms;
};

const UserTypesPage = () => {
  // const [selectedTypeId, setSelectedTypeId] = useState(null);

  // const [showCreateModal, setShowCreateModal] = useState(false);
  // const [creating, setCreating] = useState(false);

  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [view, setView] = useState("list"); // list | permissions
  const [activeRole, setActiveRole] = useState(null);
  const [permissions, setPermissions] = useState({});

  const [showModal, setShowModal] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [toast, setToast] = useState(null);

  const user = useAuthStore((s) => s.user);
  //const setPermissions = useAuthStore((s) => s.setPermissions);

  const setAuthPermissions = useAuthStore((s) => s.setPermissions);

  const fetchUserTypes = async () => {
    try {
      setLoading(true);
      const data = await getUserTypes();
      const list = Array.isArray(data) ? data : data.results || [];
      setUserTypes(list);
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  // Load roles
  useEffect(() => {
    // const fetchUserTypes = async () => {
    //   try {
    //     setLoading(true);
    //     const data = await getUserTypes();

    //     const roles = Array.isArray(data) ? data : data.results || [];

    //     setUserTypes(roles);

    //     if (roles.length > 0) {
    //       setSelectedTypeId(roles[0].id);
    //       setPermissions(roles[0].permissions || buildDefaultPermissions());
    //     }
    //   } catch (err) {
    //     setToast({
    //       type: "error",
    // message:
    //   err?.response?.data?.detail ||
    //   err?.detail ||
    //   "Something went wrong",
    //     });
    //   } finally {
    //     setLoading(false);
    //   }
    // };

    fetchUserTypes();
  }, []);

  // Handle role change
  // const handleRoleChange = (id) => {
  //   setSelectedTypeId(id);
  //   const selected = userTypes.find((t) => t.id === id);
  //   setPermissions(selected?.permissions || buildDefaultPermissions());
  // };

  // Toggle permission
  const toggleParent = (group) => {
    setPermissions((prev) => {
      const newValue = !prev[group.key];
      const updated = { ...prev, [group.key]: newValue };

      group.children.forEach((child) => {
        updated[child.key] = newValue;
      });

      return updated;
    });
  };

  const toggleChild = (group, childKey) => {
    setPermissions((prev) => {
      const updated = {
        ...prev,
        [childKey]: !prev[childKey],
      };

      // Check if ALL children are checked after toggle
      const allChildrenChecked = group.children.every((child) =>
        child.key === childKey
          ? !prev[childKey] // new value
          : prev[child.key],
      );

      updated[group.key] = allChildrenChecked;

      return updated;
    });
  };

  // const handleCreateUserType = async (name) => {
  //   try {
  //     setCreating(true);

  //     await createUserType({
  //       name,
  //       permissions: buildDefaultPermissions(),
  //     });

  //     setShowCreateModal(false);

  //     // Refresh list
  //     const data = await getUserTypes();
  //     const roles = Array.isArray(data) ? data : data.results || [];
  //     setUserTypes(roles);
  //     setSelectedTypeId(roles[roles.length - 1]?.id || null);
  //     setPermissions(
  //       roles[roles.length - 1]?.permissions || buildDefaultPermissions()
  //     );
  //   } catch {
  //     console.error("Failed to create user type");
  //     // setToast({
  //     //   type: "error",
  //     //   message: "Failed to create user type",
  //     // });
  //   } finally {
  //     setCreating(false);
  //   }
  // };

  const handleSubmitUserType = async (name) => {
    try {
      setSaving(true);

      if (editingRole) {
        await updateUserType(editingRole.id, { name });
      } else {
        await createUserType({
          name,
          permissions: buildDefaultPermissions(),
        });
      }

      setShowModal(false);
      setEditingRole(null);
      fetchUserTypes();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  // -----------------------------
  // Delete
  // -----------------------------
  const handleDelete = async (role) => {
    // const confirmed = window.confirm(
    //   `Are you sure you want to delete "${role.name}"?`
    // );
    // if (!confirmed) return;

    // try {
    //   await deleteUserType(role.id);
    //   fetchUserTypes();
    // } catch (err) {
    //   setToast({
    //     type: "error",
    //     message: err?.response?.data?.detail,
    //   });
    // }
    try {
      setDeleting(true);
      await deleteUserType(deleteUserId);

      setDeleteUserId(null);

      // Refresh users list
      fetchUserTypes();
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setDeleting(false);
    }
  };

  const openPermissions = (role) => {
    setActiveRole(role);
    setPermissions(role.permissions || buildDefaultPermissions());
    setView("permissions");
  };

  const handleSavePermissions = async () => {
    if (!activeRole) return;

    try {
      setSaving(true);

      await updateUserType(activeRole.id, {
        permissions,
      });

      if (activeRole?.id === user?.type?.id) {
        setAuthPermissions(permissions);
      }

      setToast({
        type: "success",
        message: "Permissions updated successfully",
      });

      // Update local list so UI stays in sync
      setUserTypes((prev) =>
        prev.map((role) =>
          role.id === activeRole.id ? { ...role, permissions } : role,
        ),
      );
    } catch (err) {
      setToast({
        type: "error",
        message: err?.response?.data?.detail,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-white">
            User Types & Permissions
          </h1>
          <p className="text-sm text-slate-400">
            Control access to dashboard sections by user type
          </p>
        </div>

        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 rounded-lg bg-pink-600 hover:bg-pink-700 text-white text-sm font-medium transition"
        >
          + Add User Type
        </button>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center h-64 text-slate-400">
          Loading user types...
        </div>
      )}

      {/* ---------------- LIST VIEW ---------------- */}
      {!loading && view === "list" && (
        <div className="bg-slate-900/60 border border-white/10 rounded-xl">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-slate-400">
              <tr>
                <th className="px-6 py-3 text-left">User Type</th>
                <th className="px-6 py-3 text-center">Permissions</th>
                <th className="px-6 py-3 text-center">Edit</th>
                <th className="px-6 py-3 text-center">Delete</th>
              </tr>
            </thead>

            <tbody>
              {userTypes.map((role) => (
                <tr
                  key={role.id}
                  className="border-b border-white/5 hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-4 text-white font-medium">
                    {role.name}
                  </td>

                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => openPermissions(role)}
                      className="text-pink-500 hover:text-pink-400"
                    >
                      Manage
                    </button>
                  </td>

                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => {
                        setEditingRole(role);
                        setShowModal(true);
                      }}
                      className="text-slate-300 hover:text-white"
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </td>

                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => setDeleteUserId(role.id)}
                      className="text-red-400 hover:text-red-500"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}

              {!userTypes.length && (
                <tr>
                  <td
                    colSpan={4}
                    className="px-6 py-10 text-center text-slate-400"
                  >
                    No user types found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ---------------- PERMISSIONS VIEW ---------------- */}
      {view === "permissions" && activeRole && (
        <div className="space-y-4">
          <button
            onClick={() => setView("list")}
            className="text-sm text-slate-400 hover:text-white"
          >
            ← Back to User Types
          </button>

          <h2 className="text-lg font-semibold text-white">
            Permissions for {activeRole.name}
          </h2>

          <div className="bg-slate-900/60 border border-white/10 rounded-xl">
            <div className="px-6 py-4 border-b border-white/10">
              <h2 className="text-sm font-semibold text-white uppercase tracking-wider">
                Permissions
              </h2>
            </div>

            <div className="px-6 py-4 space-y-6">
              {PERMISSIONS_SCHEMA.map((group) => (
                <div key={group.key}>
                  {/* Parent */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-white">
                      {group.label}
                    </span>
                    <input
                      type="checkbox"
                      checked={permissions[group.key]}
                      onChange={() => toggleParent(group)}
                      className="w-5 h-5 accent-pink-600 cursor-pointer"
                    />
                  </div>

                  {/* Children */}
                  <div className="ml-6 space-y-3">
                    {group.children.map((child) => (
                      <div
                        key={child.key}
                        className="flex items-center justify-between"
                      >
                        <span className="text-sm text-slate-300">
                          {child.label}
                        </span>
                        <input
                          type="checkbox"
                          checked={permissions[child.key]}
                          onChange={() => toggleChild(group, child.key)}
                          className="w-5 h-5 accent-pink-600 cursor-pointer"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="flex justify-end">
            <button
              onClick={handleSavePermissions}
              disabled={saving}
              className={`px-4 py-2 rounded-lg text-sm font-medium text-white
                ${
                  saving
                    ? "bg-pink-400 cursor-not-allowed"
                    : "bg-pink-600 hover:bg-pink-700"
                }
              `}
            >
              {saving ? "Saving..." : "Save Permissions"}
            </button>
          </div>
        </div>
      )}

      <AddUserTypeModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingRole(null);
        }}
        onSubmit={handleSubmitUserType}
        loading={saving}
        update={editingRole ? true : false}
        initialName={editingRole?.name}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteUserId}
        onClose={() => setDeleteUserId(null)}
        onConfirm={handleDelete}
        loading={deleting}
      />

      <ToastContainer>
        {toast && (
          <div className="toast-enter">
            <Toast
              type={toast.type}
              message={toast.message}
              onClose={() => setToast(null)}
            />
          </div>
        )}
      </ToastContainer>
    </div>
  );
};

export default UserTypesPage;
